import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';

import { ProjectPaymentLimit } from '@/app/models/projectPaymentLimit';
import { TablePagination, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import ActionMenu, { ItemAction } from '../common/ActionMenu';
import StandardDialog from '../common/StandardDialog';

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: 13,
  fontWeight: 'bold',
  paddingBottom: 1,
}));

const RowTableCell = styled(TableCell)({
  fontSize: 13,
});

type ProjectExceptionsTableProps = {
  rows: Array<ProjectPaymentLimit>;
  setEditProject: (s: string) => void;
  setShowCreateModal: (b: boolean) => void;
  handleDeleteAll: (s: string) => void;
  count: number;
  page: number;
  rowsPerPage: number;

  setPage: (n: number) => void;
  setRowsPerPage: (n: number) => void;
};

function ProjectExceptionsTable(props: ProjectExceptionsTableProps) {
  const {
    rows,
    setEditProject,
    setShowCreateModal,
    handleDeleteAll,
    count,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  } = props;
  const { t } = useTranslation(['exceptions', 'common']);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteAllCode, setDeleteAllCode] = useState(null as unknown as string);
  return (
    <>
      <StandardDialog
        title={t('synchronize')}
        contentText={`${t('are.you.sure.you.want.to.delete.all.exception.cases.from.this.project')}`}
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
        }}
        onConfirm={() => {
          setShowDeleteDialog(false);
          handleDeleteAll(deleteAllCode);
        }}
        cancelText={t('common:no')}
        confirmText={t('common:yes')}
      />
      <TableContainer>
        <Table size="small" aria-label="Exception Case Table">
          <TableHead>
            <TableRow>
              <HeaderTableCell>{t('common:payer.project')}</HeaderTableCell>
              <HeaderTableCell>{t('destiny.projects')}</HeaderTableCell>
              <HeaderTableCell>{t('common:actions')}</HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.payingProject.code}>
                <RowTableCell>{`${row.payingProject.code} - ${row.payingProject.name}`}</RowTableCell>

                <RowTableCell>
                  <Tooltip title={row.projects.toString().length > 100 ? row.projects.toString() : ''}>
                    <p>
                      {row.projects.toString().length > 100
                        ? `${row.projects.toString().slice(0, 100)}...`
                        : row.projects.toString()}
                    </p>
                  </Tooltip>
                </RowTableCell>

                <RowTableCell>
                  <ActionMenu>
                    <ItemAction
                      text={t('common:edit.rules')}
                      Icon={EditIcon}
                      onClick={() => {
                        setEditProject(row.payingProject.code);
                        setShowCreateModal(true);
                      }}
                    />
                    <ItemAction
                      text={t('common:delete.rules')}
                      Icon={DeleteIcon}
                      onClick={() => {
                        setDeleteAllCode(row.payingProject.code);
                        setShowDeleteDialog(true);
                      }}
                    />
                  </ActionMenu>
                </RowTableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={8}>
                  <Typography variant="subtitle2">{t('common:there.are.no.exception.cases.added.yet')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          sx={{ width: '100%' }}
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelDisplayedRows={({ from, to, count: limit }) => `${from}-${to} ${t('common:of')} ${limit}`}
          labelRowsPerPage={t('common:items.per.page')}
        />
      </TableContainer>
    </>
  );
}

export default ProjectExceptionsTable;
